import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type {
  CatalogItem,
  Quote,
  QuoteItem,
  SaveQuote,
  SaveQuoteItem,
} from "@breeze/shared";
import { WORK_ORDER_STATUSES } from "@breeze/shared";
import type { Technician } from "@breeze/shared";
import { useCatalog, useQuote, useSaveQuote } from "../hooks/useQuote.js";
import { useSlotsQuery } from "../hooks/useSlotsQuery.js";
import {
  createAccountContact,
  createAccountLocation,
  fetchAccountCluster,
  fetchCardCluster,
  fetchTechNotes,
  fetchTechniciansForPostcode,
  initiateCall,
  postTechNote,
} from "../api.js";
import type {
  AccountContact,
  AccountLocation,
  ClusterAccount,
} from "@breeze/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { pcaFind, pcaRetrieve } from "../pca.js";
import type { PcaFindItem } from "../pca.js";
import { NotifyModal } from "./NotifyModal.js";
import { FindSlotsModal } from "./FindSlotsModal.js";
import { DispatchBoard5 } from "./DispatchBoard5.js";

type Props = {
  vid: number | null;
  onClose: () => void;
};

type ContactDraft = {
  firstName: string;
  lastName: string;
  email: string;
  // `phone` is the legacy single-field input kept around for back-compat.
  // The four typed fields below are the source of truth for new edits;
  // saveQuote persists all of them to B_TCONTACT.TELEPHONE_*.
  phone: string;
  phoneMobile: string;
  phoneHome: string;
  phoneWork: string;
  phoneOther: string;
};

type LocationDraft = {
  address1: string;
  address2: string;
  address3: string;
  city: string;
  postcode: string;
};

type ScheduleDraft = {
  workOrderStatus: string;
  scheduleDate: string; // "" when no date set
  startTime: string; // HH:MM or ""
  endTime: string; // HH:MM or ""
  technicianId: string; // B_TUSERS.ID as string, "" when unassigned
};

type Draft = {
  items: QuoteItem[]; // keeps full metadata for display; quantity 0 means removed
  contact: ContactDraft;
  location: LocationDraft;
  schedule: ScheduleDraft;
  // Which account in the cluster the visit is bound to. Drives the
  // contact + address pickers (filtered to this account's links) and
  // is repointed onto B_TVISIT.ACCOUNTID at save. Order-form.php
  // expects visit + contact + location to all belong to one account.
  accountAid: number | null;
  // Tracks which existing CID / LID the visit is currently pointing at.
  // When the picker swaps, this changes and the save payload tells the
  // API to repoint B_TVISIT.SERVICECONTACTID / SERVICELOCATIONID.
  serviceContactId: number | null;
  serviceLocationId: number | null;
  // Billing pointers persisted on B_TINVOICE. When `differentBilling*`
  // is false the form mirrors service → billing on save; when true the
  // dropdowns drive the values independently.
  differentBillingContact: boolean;
  differentBillingLocation: boolean;
  billingContactId: number | null;
  billingLocationId: number | null;
};

// Trim STARTTIME/ENDTIME to HH:MM for the <input type="time"> fields.
// Breeze stamps "00:00:00" or "" when unset; both normalise to "".
function trimTime(t: string | null): string {
  if (!t) return "";
  const hhmm = t.trim().slice(0, 5);
  return /^\d{2}:\d{2}$/.test(hhmm) ? hhmm : "";
}

function quoteToDraft(q: Quote): Draft {
  return {
    items: q.items.map((i) => ({ ...i })),
    contact: {
      firstName: q.contact.firstName,
      lastName: q.contact.lastName,
      email: q.contact.email,
      phone: q.contact.phone,
      phoneMobile: q.contact.phoneMobile ?? q.contact.phone ?? "",
      phoneHome: q.contact.phoneHome ?? "",
      phoneWork: q.contact.phoneWork ?? "",
      phoneOther: q.contact.phoneOther ?? "",
    },
    location: {
      address1: q.location.address1,
      address2: q.location.address2,
      address3: q.location.address3,
      city: q.location.city,
      postcode: q.location.postcode,
    },
    accountAid: q.aid > 0 ? q.aid : null,
    serviceContactId: q.contact.cid,
    serviceLocationId: q.location.lid,
    // Billing differs from service when the persisted invoice pointer
    // doesn't match the visit's service pointer. Initialised here so
    // the toggle reflects the on-disk state on load.
    differentBillingContact:
      q.billingContactId !== null && q.billingContactId !== q.contact.cid,
    differentBillingLocation:
      q.billingLocationId !== null && q.billingLocationId !== q.location.lid,
    billingContactId: q.billingContactId ?? q.contact.cid,
    billingLocationId: q.billingLocationId ?? q.location.lid,
    schedule: {
      workOrderStatus: q.workOrderStatus ?? "Booking not made (Qualified)",
      scheduleDate: q.scheduleDate ?? "",
      startTime: trimTime(q.startTime),
      endTime: trimTime(q.endTime),
      technicianId: q.technicianId ?? "",
    },
  };
}

function contactEquals(a: ContactDraft, b: ContactDraft): boolean {
  return (
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.email === b.email &&
    a.phone === b.phone &&
    a.phoneMobile === b.phoneMobile &&
    a.phoneHome === b.phoneHome &&
    a.phoneWork === b.phoneWork &&
    a.phoneOther === b.phoneOther
  );
}

function locationEquals(a: LocationDraft, b: LocationDraft): boolean {
  return (
    a.address1 === b.address1 &&
    a.address2 === b.address2 &&
    a.address3 === b.address3 &&
    a.city === b.city &&
    a.postcode === b.postcode
  );
}

function scheduleEquals(a: ScheduleDraft, b: ScheduleDraft): boolean {
  return (
    a.workOrderStatus === b.workOrderStatus &&
    a.scheduleDate === b.scheduleDate &&
    a.startTime === b.startTime &&
    a.endTime === b.endTime &&
    a.technicianId === b.technicianId
  );
}

function draftEquals(a: Draft, b: Draft): boolean {
  if (a.accountAid !== b.accountAid) return false;
  if (a.serviceContactId !== b.serviceContactId) return false;
  if (a.serviceLocationId !== b.serviceLocationId) return false;
  if (a.differentBillingContact !== b.differentBillingContact) return false;
  if (a.differentBillingLocation !== b.differentBillingLocation) return false;
  if (a.billingContactId !== b.billingContactId) return false;
  if (a.billingLocationId !== b.billingLocationId) return false;
  if (!contactEquals(a.contact, b.contact)) return false;
  if (!locationEquals(a.location, b.location)) return false;
  if (!scheduleEquals(a.schedule, b.schedule)) return false;
  if (a.items.length !== b.items.length) return false;
  for (let i = 0; i < a.items.length; i++) {
    const x = a.items[i]!;
    const y = b.items[i]!;
    if (
      x.chargemasterId !== y.chargemasterId ||
      x.quantity !== y.quantity ||
      x.unitPrice !== y.unitPrice ||
      x.description !== y.description ||
      x.discountAmount !== y.discountAmount ||
      x.discountPercent !== y.discountPercent ||
      x.discountType !== y.discountType ||
      x.discountSplit !== y.discountSplit ||
      x.itemCondition !== y.itemCondition ||
      x.isTaxable !== y.isTaxable ||
      x.taxRate !== y.taxRate
    ) {
      return false;
    }
  }
  return true;
}

function draftToSaveItems(draft: Draft): SaveQuoteItem[] {
  return draft.items
    .filter((it) => it.quantity > 0)
    .map((it) => ({
      chargemasterId: it.chargemasterId,
      description: it.description ?? "",
      quantity: it.quantity,
      unitPrice: it.unitPrice,
      isTaxable: it.isTaxable,
      taxRate: it.taxRate,
      priceCodeId: it.priceCodeId,
      itemCondition: it.itemCondition ?? "Average",
      discountType: it.discountType ?? "Fixed Amount",
      discountAmount: it.discountAmount,
      discountPercent: it.discountPercent,
      discountSplit: it.discountSplit ?? "breeze",
    }));
}

function computeTotals(items: QuoteItem[]): {
  subtotal: number;
  tax: number;
  discountTotal: number;
  total: number;
} {
  let subtotal = 0;
  let tax = 0;
  let discountTotal = 0;
  for (const it of items) {
    if (it.quantity <= 0) continue;
    const rawSub = it.quantity * it.unitPrice;
    const d =
      it.discountType === "Percentage"
        ? rawSub * (it.discountPercent / 100)
        : it.discountAmount;
    const postDiscount = Math.max(0, rawSub - d);
    subtotal += postDiscount;
    discountTotal += d;
    if (it.isTaxable) tax += postDiscount * (it.taxRate / 100);
  }
  return {
    subtotal: round2(subtotal),
    tax: round2(tax),
    discountTotal: round2(discountTotal),
    total: round2(subtotal + tax),
  };
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Human-readable time range for the sales script. Mirrors quick-form.php:
// round total minutes up to 30-min granularity, show a "lo to hi" range.
function formatMinutes(m: number): string {
  if (m < 60) return `${m} minutes`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (r === 0) return `${h} hour${h > 1 ? "s" : ""}`;
  const hasHalf = r === 30;
  return `${h}${hasHalf ? " and a half" : ""} hour${h > 1 || (h === 1 && r > 0) ? "s" : ""}`;
}

function timeRangeLabel(totalMinutes: number): string {
  let rounded = Math.round(totalMinutes / 30) * 30;
  if (rounded < 30) rounded = 30;
  return `${formatMinutes(rounded)} to ${formatMinutes(rounded + 30)}`;
}

// Aggregate protector upsell: for every regular item that has a protector
// variant the user hasn't selected, swap in the protector's price to get
// the "with-protector" total. Used for the banner + percentage delta.
function computeProtectorUpsell(
  items: QuoteItem[],
  protectorMap: Record<string, string>,
  catalog: Map<string, CatalogItem>
): { total: number; hasAny: boolean } {
  let sub = 0;
  let vat = 0;
  let hasAny = false;
  for (const it of items) {
    if (it.quantity <= 0) continue;
    const protId = protectorMap[it.chargemasterId];
    const alreadySelected = items.some(
      (x) => x.chargemasterId === protId && x.quantity > 0
    );
    if (protId && !alreadySelected) {
      const prot = catalog.get(protId);
      if (prot) {
        const pLs = it.quantity * prot.price;
        sub += pLs;
        if (prot.isTaxable) vat += pLs * (prot.taxRate / 100);
        hasAny = true;
        continue;
      }
    }
    // No upsell available — carry through the current line's price.
    const rawSub = it.quantity * it.unitPrice;
    const d =
      it.discountType === "Percentage"
        ? rawSub * (it.discountPercent / 100)
        : it.discountAmount;
    const postDiscount = Math.max(0, rawSub - d);
    sub += postDiscount;
    if (it.isTaxable) vat += postDiscount * (it.taxRate / 100);
  }
  return { total: round2(sub + vat), hasAny };
}

export function QuoteEditor({ vid, onClose }: Props): JSX.Element {
  const q = useQuote(vid);

  useEffect(() => {
    if (vid === null) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [vid, onClose]);

  return (
    <AnimatePresence>
      {vid !== null ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-50 bg-ink-950/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="editor"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="relative w-full max-w-5xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              {q.isLoading ? (
                <div className="px-6 py-10 text-center text-ink-200">
                  loading quote…
                </div>
              ) : q.isError || !q.data ? (
                <div className="px-6 py-10 text-center text-slack-pink">
                  couldn't load quote
                  <button
                    onClick={onClose}
                    className="ml-3 rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:text-ink-100"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <QuoteBody quote={q.data.quote} vid={vid} onClose={onClose} />
              )}
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function QuoteBody({
  quote,
  vid,
  onClose,
}: {
  quote: Quote;
  vid: number;
  onClose: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => quoteToDraft(quote));
  // A kanban card can span a CLUSTER of accounts linked by shared
  // email/phone (mirrors trengo3.php's matching), not just the single
  // ACCOUNTID on B_TVISIT. Pull the full cluster so the contact / address
  // pickers see every related contact + location, the same way the
  // Slideout's Account tab does. Falls back to the account-seeded
  // cluster if the quote has no kanban card yet (brand-new quote).
  const clusterQ = useQuery({
    queryKey:
      quote.cardId !== null
        ? ["cluster", quote.cardId]
        : ["accountCluster", quote.aid],
    queryFn: async () => {
      if (quote.cardId !== null) {
        const r = await fetchCardCluster(quote.cardId);
        return r.cluster;
      }
      const r = await fetchAccountCluster(quote.aid);
      return r.cluster;
    },
    enabled: quote.cardId !== null || quote.aid > 0,
    staleTime: 30_000,
  });
  // List of accounts in the cluster — drives the Account dropdown.
  const clusterAccounts: ClusterAccount[] = clusterQ.data?.accounts ?? [];
  // Contact / address pickers filter to whatever account is currently
  // active on the draft so the visit stays coherent (one account →
  // its own contacts + its own addresses).
  const activeAid = draft.accountAid;
  const accountContacts: AccountContact[] = useMemo(
    () =>
      (clusterQ.data?.contacts ?? [])
        .filter((c) => activeAid === null || c.aid === activeAid)
        .map((c) => ({
          cid: c.cid,
          fullName: c.fullName,
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email,
          phoneMobile: c.phoneMobile,
          phoneHome: c.phoneHome,
          phoneWork: c.phoneWork,
          phoneOther: c.phoneOther,
        })),
    [clusterQ.data, activeAid]
  );
  const accountLocations: AccountLocation[] = useMemo(
    () =>
      (clusterQ.data?.addresses ?? [])
        .filter((a) => activeAid === null || a.aid === activeAid)
        .map((a) => ({
          lid: a.lid,
          address1: a.address1,
          address2: a.address2,
          address3: a.address3,
          city: a.city,
          postcode: a.postcode,
        })),
    [clusterQ.data, activeAid]
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailIdx, setDetailIdx] = useState<number | null>(null);
  const [whatToSayOpen, setWhatToSayOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const save = useSaveQuote(vid);
  // Load catalog eagerly so the detail modal is instant and we can resolve
  // protector pairings for the upsell nudge.
  const cat = useCatalog(true);

  const catalogIndex = useMemo(() => {
    const m = new Map<string, CatalogItem>();
    if (!cat.data) return m;
    for (const c of cat.data.categories) for (const it of c.items) m.set(it.chargemasterId, it);
    return m;
  }, [cat.data]);
  const protectorMap = cat.data?.protectorMap ?? {};

  // If the server-side quote changes (e.g. SSE refetch after another user
  // saved), resync the draft but only if the user has no local edits.
  useEffect(() => {
    setDraft((d) => (draftEquals(d, quoteToDraft(quote)) ? d : d));
    // Only reset when the vid changes (opening a different quote).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid]);

  const dirty = !draftEquals(draft, quoteToDraft(quote));
  const itemsWithQty = draft.items.filter((i) => i.quantity > 0);
  const totals = computeTotals(draft.items);
  const MIN_CHARGE = quote.totals.minimumCharge;
  const totalMinutes = draft.items.reduce(
    (s, it) => s + (it.quantity > 0 ? it.quantity * (it.estimatedMinutes ?? 0) : 0),
    0
  );

  // Travel-category chargemaster IDs — these are pass-through (tech keeps 100%)
  // so they don't count toward the tech's commissionable earnings on the job.
  // Pulled from B_TJOBITEMCATEGORY where NAME='Travel'.
  const travelChargemasterIds = useMemo(() => {
    const ids = new Set<string>();
    if (!cat.data) return ids;
    for (const c of cat.data.categories) {
      if (c.name.trim().toLowerCase() === "travel") {
        for (const it of c.items) ids.add(it.chargemasterId);
      }
    }
    return ids;
  }, [cat.data]);

  // Min-charge logic: £60 applies to the cleaning subtotal alone.
  // Travel sits on top of the bumped cleaning total. Both numbers are
  // inc-VAT to match how MIN_CHARGE is expressed.
  const splitTotals = useMemo(() => {
    let cleaningInc = 0;
    let travelInc = 0;
    for (const it of draft.items) {
      if (it.quantity <= 0) continue;
      const rawSub = it.quantity * it.unitPrice;
      const d =
        it.discountType === "Percentage"
          ? rawSub * (it.discountPercent / 100)
          : it.discountAmount;
      const postDiscount = Math.max(0, rawSub - d);
      const inc =
        postDiscount + (it.isTaxable ? postDiscount * (it.taxRate / 100) : 0);
      if (travelChargemasterIds.has(it.chargemasterId)) travelInc += inc;
      else cleaningInc += inc;
    }
    return { cleaning: round2(cleaningInc), travel: round2(travelInc) };
  }, [draft.items, travelChargemasterIds]);

  const isBelowMin =
    totals.total > 0 && splitTotals.cleaning > 0 && splitTotals.cleaning < MIN_CHARGE;
  const minBump = isBelowMin ? round2(MIN_CHARGE - splitTotals.cleaning) : 0;
  const displayTotal = round2(splitTotals.cleaning + minBump + splitTotals.travel);

  // Value the tech earns commission on — drives the £15/hour gate AND the
  // hourly-earnings column in the Find Slots table. Falls back to displayTotal
  // if the catalog hasn't loaded yet (no Travel items to exclude).
  //
  // Split-discount accounting: lines marked discountSplit="split" pass half
  // the discount onto the tech (tech absorbs 50%), so the tech's billable
  // line value becomes rawSub - discount/2 instead of rawSub - discount.
  // "breeze" lines leave the tech's value at the full post-discount amount
  // because Breeze takes the entire hit.
  const billableValueGbp = useMemo(() => {
    if (travelChargemasterIds.size === 0) return displayTotal;
    let s = 0;
    for (const it of draft.items) {
      if (it.quantity <= 0) continue;
      if (travelChargemasterIds.has(it.chargemasterId)) continue;
      const rawSub = it.quantity * it.unitPrice;
      const d =
        it.discountType === "Percentage"
          ? rawSub * (it.discountPercent / 100)
          : it.discountAmount;
      const techDiscount = it.discountSplit === "split" ? d / 2 : 0;
      const techNet = Math.max(0, rawSub - techDiscount);
      s += techNet;
      if (it.isTaxable) s += techNet * (it.taxRate / 100);
    }
    return round2(s);
  }, [draft.items, travelChargemasterIds, displayTotal]);

  // Background prefetch of slot search — fires when the form has a full
  // postcode + duration + value, so by the time the user clicks Find Slots
  // or Dispatch Board the result is already in the React Query cache.
  // Re-runs (debounced 500ms) when the user edits postcode / duration /
  // value. Keystroke noise is filtered out by the regex gate inside the hook.
  useSlotsQuery({
    postcode: draft.location.postcode,
    durationMinutes: totalMinutes > 0 ? totalMinutes : 60,
    estimatedValueGbp: billableValueGbp,
    jobRef: vid !== null ? `VID-${vid}` : `card-new`,
  });
  const upsell = computeProtectorUpsell(draft.items, protectorMap, catalogIndex);
  const upsellPctMore =
    upsell.hasAny && displayTotal > 0
      ? Math.round(((upsell.total - displayTotal) / displayTotal) * 100)
      : 0;

  function updateItem(idx: number, patch: Partial<QuoteItem>): void {
    setDraft((d) => {
      const cur = d.items[idx];
      if (!cur) return d;
      const next = d.items.slice();
      next[idx] = { ...cur, ...patch };
      return { ...d, items: next };
    });
  }

  function addCatalogItem(c: CatalogItem): void {
    setDraft((d) => {
      // If an existing row for this chargemaster is present, bump its qty.
      const existingIdx = d.items.findIndex(
        (it) => it.chargemasterId === c.chargemasterId
      );
      if (existingIdx >= 0) {
        const cur = d.items[existingIdx]!;
        const next = d.items.slice();
        next[existingIdx] = { ...cur, quantity: Math.max(1, cur.quantity + 1) };
        return { ...d, items: next };
      }
      const newItem: QuoteItem = {
        chargeId: null,
        chargemasterId: c.chargemasterId,
        name: c.name,
        description: c.description ?? c.name,
        quantity: 1,
        unitPrice: c.price,
        isTaxable: c.isTaxable,
        taxRate: c.taxRate,
        priceCodeId: c.priceCodeId,
        itemCondition: "Average",
        discountType: "Fixed Amount",
        discountAmount: 0,
        discountPercent: 0,
        discountSplit: "breeze",
        displayOrder: d.items.length + 1,
        estimatedMinutes: c.estimatedMinutes,
      };
      return { ...d, items: [...d.items, newItem] };
    });
    setPickerOpen(false);
  }

  // Swap a regular item for its protector variant at the same quantity.
  // The protector line includes cleaning + protection, so we fully replace
  // the regular row (setting its qty to 0) rather than stacking two lines.
  function swapForProtector(regularIdx: number, prot: CatalogItem): void {
    setDraft((d) => {
      const reg = d.items[regularIdx];
      if (!reg) return d;
      const qty = reg.quantity;
      const condition = reg.itemCondition ?? "Average";
      const next = d.items.slice();
      next[regularIdx] = { ...reg, quantity: 0 };
      const existingProtIdx = next.findIndex(
        (it) => it.chargemasterId === prot.chargemasterId
      );
      if (existingProtIdx >= 0) {
        const cur = next[existingProtIdx]!;
        next[existingProtIdx] = {
          ...cur,
          quantity: Math.max(1, cur.quantity + qty),
          itemCondition: condition,
        };
      } else {
        next.push({
          chargeId: null,
          chargemasterId: prot.chargemasterId,
          name: prot.name,
          description: prot.description ?? prot.name,
          quantity: qty,
          unitPrice: prot.price,
          isTaxable: prot.isTaxable,
          taxRate: prot.taxRate,
          priceCodeId: prot.priceCodeId,
          itemCondition: condition,
          discountType: "Fixed Amount",
          discountAmount: 0,
          discountPercent: 0,
          discountSplit: "breeze",
          displayOrder: next.length + 1,
          estimatedMinutes: prot.estimatedMinutes,
        });
      }
      return { ...d, items: next };
    });
  }

  async function onSave(): Promise<void> {
    setSaveError(null);
    try {
      const original = quoteToDraft(quote);
      const payload: SaveQuote = { items: draftToSaveItems(draft) };
      // Account swap → repoint B_TVISIT.ACCOUNTID. Contact/location
      // changes flow through the normal patch paths below.
      if (draft.accountAid !== original.accountAid) {
        payload.accountAid = draft.accountAid;
      }
      // If the picker swapped to a different existing contact / location,
      // tell the API to repoint B_TVISIT.SERVICE*ID. The contact/location
      // patch (if any) will then target the newly-pointed-at row.
      if (draft.serviceContactId !== original.serviceContactId) {
        payload.serviceContactId = draft.serviceContactId;
      }
      if (draft.serviceLocationId !== original.serviceLocationId) {
        payload.serviceLocationId = draft.serviceLocationId;
      }
      // Billing pointers — sent when they differ from the on-disk values.
      // When the "Different billing" toggle is off, billingContactId
      // mirrors serviceContactId in the draft, so this still flips the
      // invoice row back to service-on-save if staff just unchecked it.
      if (
        draft.billingContactId !== original.billingContactId &&
        draft.billingContactId !== null
      ) {
        payload.billingContactId = draft.billingContactId;
      }
      if (
        draft.billingLocationId !== original.billingLocationId &&
        draft.billingLocationId !== null
      ) {
        payload.billingLocationId = draft.billingLocationId;
      }
      if (
        !contactEquals(draft.contact, original.contact) ||
        payload.serviceContactId !== undefined
      ) {
        payload.contact = draft.contact;
      }
      if (
        !locationEquals(draft.location, original.location) ||
        payload.serviceLocationId !== undefined
      ) {
        payload.location = draft.location;
      }
      if (!scheduleEquals(draft.schedule, original.schedule)) {
        payload.schedule = {
          workOrderStatus: draft.schedule.workOrderStatus,
          // Empty string ⇒ null so MySQL clears the DATE column rather
          // than choking on an invalid value.
          scheduleDate: draft.schedule.scheduleDate.trim() || null,
          startTime: draft.schedule.startTime,
          endTime: draft.schedule.endTime,
          technicianId: draft.schedule.technicianId,
        };
      }
      const res = await save.mutateAsync(payload);
      // Reset draft from the server's canonical response so "Unsaved changes"
      // clears. Without this, zero-qty rows + server-defaulted fields
      // (e.g. discountType) leave a phantom diff.
      setDraft(quoteToDraft(res.quote));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "save failed");
    }
  }

  function onCancel(): void {
    setDraft(quoteToDraft(quote));
    setSaveError(null);
  }

  return (
    <>
      <header className="flex items-start justify-between gap-3 border-b border-ink-700 px-6 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-[22px] font-semibold leading-tight text-ink-50">
            {quote.visitNumberText
              ? `Order #${quote.visitNumberText}`
              : "New quote"}
          </h2>
          {quote.visitId ? (
            <div className="mt-0.5 font-mono text-[11px] text-ink-300">
              {quote.visitId}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {quote.vid !== null ? (
            <>
              <button
                onClick={() =>
                  window.open(
                    `https://dev.breeze.services/order-form.php?id=${quote.vid}`,
                    `order_${quote.vid}`,
                    "width=1200,height=750,scrollbars=1,resizable=1"
                  )
                }
                className="rounded-md border border-ink-500 bg-ink-800 px-2 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                title="Open the full order form in a new window"
              >
                Full Form
              </button>
              <button
                onClick={() => setNotifyOpen(true)}
                disabled={quote.cardId === null}
                className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  quote.cardId === null
                    ? "No kanban card for this visit yet — save the quote first."
                    : "Send a templated SMS / email notification via the kanban pipeline"
                }
              >
                Notify
              </button>
            </>
          ) : null}
          <button
            onClick={onClose}
            className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:border-ink-400"
            aria-label="Close"
          >
            Close
          </button>
        </div>
      </header>

      {clusterAccounts.length > 1 ? (
        <div className="flex items-center gap-2 border-t border-ink-700 px-6 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-200">
            Account
          </span>
          <select
            className="rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
            value={draft.accountAid ?? ""}
            onChange={(e) => {
              const newAid = Number(e.target.value);
              const cluster = clusterQ.data;
              const firstC =
                cluster?.contacts.find((c) => c.aid === newAid) ?? null;
              const firstL =
                cluster?.addresses.find((a) => a.aid === newAid) ?? null;
              setDraft((d) => ({
                ...d,
                accountAid: newAid,
                // Auto-pick the new account's first contact + address so the
                // visit stays coherent. Staff can pick a different one from
                // the dropdowns if needed.
                serviceContactId: firstC?.cid ?? null,
                serviceLocationId: firstL?.lid ?? null,
                contact: firstC
                  ? {
                      ...d.contact,
                      firstName: firstC.firstName,
                      lastName: firstC.lastName,
                      email: firstC.email,
                      phone: firstC.phoneMobile,
                      phoneMobile: firstC.phoneMobile,
                      phoneHome: firstC.phoneHome,
                      phoneWork: firstC.phoneWork,
                      phoneOther: firstC.phoneOther,
                    }
                  : d.contact,
                location: firstL
                  ? {
                      ...d.location,
                      address1: firstL.address1,
                      address2: firstL.address2,
                      address3: firstL.address3,
                      city: firstL.city,
                      postcode: firstL.postcode,
                    }
                  : d.location,
              }));
            }}
            title="Switch this visit to a different account in the cluster"
          >
            {clusterAccounts.map((a) => (
              <option key={a.aid} value={a.aid}>
                {a.accountName || `Account #${a.aid}`}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-ink-300">
            Contact + address pickers below filter to this account.
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
        <ContactSection
          contact={draft.contact}
          selectedCid={draft.serviceContactId}
          options={accountContacts}
          aid={draft.accountAid ?? quote.aid}
          cardId={quote.cardId}
          onChange={(patch) =>
            setDraft((d) => ({ ...d, contact: { ...d.contact, ...patch } }))
          }
          onPickExisting={(c) =>
            setDraft((d) => ({
              ...d,
              serviceContactId: c.cid,
              contact: {
                ...d.contact,
                firstName: c.firstName,
                lastName: c.lastName,
                email: c.email,
                phone: c.phoneMobile,
                phoneMobile: c.phoneMobile,
                phoneHome: c.phoneHome,
                phoneWork: c.phoneWork,
                phoneOther: c.phoneOther ?? "",
              },
            }))
          }
          onContactCreated={(newCid, fields) =>
            setDraft((d) => ({
              ...d,
              serviceContactId: newCid,
              contact: { ...d.contact, ...fields },
            }))
          }
          differentBilling={draft.differentBillingContact}
          billingCid={draft.billingContactId}
          onToggleDifferentBilling={(on) =>
            setDraft((d) => ({
              ...d,
              differentBillingContact: on,
              // When toggled off, mirror billing back to service so the
              // saveQuote payload doesn't try to repoint the invoice
              // to a stale CID.
              billingContactId: on ? d.billingContactId : d.serviceContactId,
            }))
          }
          onPickBilling={(cid) =>
            setDraft((d) => ({ ...d, billingContactId: cid }))
          }
        />
        <LocationSection
          location={draft.location}
          selectedLid={draft.serviceLocationId}
          options={accountLocations}
          aid={draft.accountAid ?? quote.aid}
          cardId={quote.cardId}
          onChange={(patch) =>
            setDraft((d) => ({ ...d, location: { ...d.location, ...patch } }))
          }
          onApplyPcaAddress={(a) =>
            setDraft((d) => ({ ...d, location: { ...d.location, ...a } }))
          }
          onPickExisting={(l) =>
            setDraft((d) => ({
              ...d,
              serviceLocationId: l.lid,
              location: {
                ...d.location,
                address1: l.address1,
                address2: l.address2,
                address3: l.address3 ?? "",
                city: l.city,
                postcode: l.postcode,
              },
            }))
          }
          onLocationCreated={(newLid, fields) =>
            setDraft((d) => ({
              ...d,
              serviceLocationId: newLid,
              location: { ...d.location, ...fields },
            }))
          }
          differentBilling={draft.differentBillingLocation}
          billingLid={draft.billingLocationId}
          onToggleDifferentBilling={(on) =>
            setDraft((d) => ({
              ...d,
              differentBillingLocation: on,
              billingLocationId: on ? d.billingLocationId : d.serviceLocationId,
            }))
          }
          onPickBilling={(lid) =>
            setDraft((d) => ({ ...d, billingLocationId: lid }))
          }
        />
      </div>

      <ScheduleSection
        vid={quote.vid}
        cardId={quote.cardId}
        schedule={draft.schedule}
        postcode={draft.location.postcode}
        estimatedValueGbp={billableValueGbp}
        durationMinutes={totalMinutes > 0 ? totalMinutes : 60}
        onChange={(patch) =>
          setDraft((d) => ({ ...d, schedule: { ...d.schedule, ...patch } }))
        }
      />

      {quote.vid !== null ? <TechNotesSection vid={quote.vid} /> : null}

      <section className="border-t border-ink-700 px-6 py-5">
        {(() => {
          // Pricing is offer-code driven, and offer code is derived from the
          // visit's service-location postcode. Adding items before there's
          // a postcode risks landing the wrong £-per-item (the catalog
          // can't pick the right offer code). All active postcodes
          // currently map to SP9903 — once you have multiple offer codes
          // per region, the catalog needs a postcode→offer lookup
          // (TODO in repos/quotes.ts:getCatalog).
          const hasPostcode = draft.location.postcode.trim().length > 0;
          return (
            <>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                  Line items
                </div>
                <button
                  onClick={() => setPickerOpen(true)}
                  disabled={!hasPostcode}
                  title={
                    hasPostcode
                      ? "Add a line item from the catalog"
                      : "Set a postcode on this visit before adding line items — pricing depends on it."
                  }
                  className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:border-ink-600 disabled:bg-ink-700 disabled:text-ink-300"
                >
                  + Add item
                </button>
              </div>
              {!hasPostcode ? (
                <div className="mb-3 rounded-md border border-slack-yellow/40 bg-slack-yellow/10 px-3 py-2 text-[12px] text-slack-yellow">
                  Add the customer's postcode in the address section above before adding line items — pricing is region-specific.
                </div>
              ) : null}
            </>
          );
        })()}
        {itemsWithQty.length === 0 ? (
          <div className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-4 py-8 text-center text-[13px] text-ink-200">
            No items on this quote yet. Click + Add item.
          </div>
        ) : (
          <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
            {draft.items.map((it, idx) => {
              if (it.quantity <= 0) return null;
              const protCmId = protectorMap[it.chargemasterId];
              const prot = protCmId ? catalogIndex.get(protCmId) : undefined;
              const protAlreadyAdded = prot
                ? draft.items.some(
                    (x) => x.chargemasterId === prot.chargemasterId && x.quantity > 0
                  )
                : false;
              // Description dirty flag: the catalog default vs. what's on
              // this line. Drives the "Description changed" pill next to
              // the item name (mirrors trengo/quick-form.php).
              const catalog = catalogIndex.get(it.chargemasterId);
              const descChanged =
                !!catalog &&
                (catalog.description ?? "") !== (it.description ?? "");
              return (
                <EditableRow
                  key={`${it.chargemasterId}-${idx}`}
                  item={it}
                  descriptionChanged={descChanged}
                  protector={prot && !protAlreadyAdded ? prot : undefined}
                  onQtyChange={(qty) => updateItem(idx, { quantity: qty })}
                  onRemove={() => updateItem(idx, { quantity: 0 })}
                  onOpenDetails={() => setDetailIdx(idx)}
                  onAddProtector={prot ? () => swapForProtector(idx, prot) : undefined}
                />
              );
            })}
          </ul>
        )}
      </section>

      {/* Totals sit directly under the line items for quick reference. */}
      <section className="border-t border-ink-700 bg-ink-900/60 px-6 py-4">
        <dl className="ml-auto grid max-w-xs grid-cols-[1fr_auto] gap-y-1 text-[13px]">
          <dt className="text-ink-200">Subtotal</dt>
          <dd className="tabular-nums text-ink-50">
            £{totals.subtotal.toFixed(2)}
          </dd>
          {totals.discountTotal > 0 ? (
            <>
              <dt className="text-ink-200">Discount</dt>
              <dd className="tabular-nums text-slack-yellow">
                −£{totals.discountTotal.toFixed(2)}
              </dd>
            </>
          ) : null}
          <dt className="text-ink-200">VAT</dt>
          <dd className="tabular-nums text-ink-50">
            £{totals.tax.toFixed(2)}
          </dd>
          {isBelowMin ? (
            <>
              <dt className="text-ink-200">
                Min charge (£{MIN_CHARGE.toFixed(2)})
              </dt>
              <dd className="tabular-nums text-slack-yellow">
                +£{minBump.toFixed(2)}
              </dd>
            </>
          ) : null}
          <dt className="text-[15px] font-semibold text-ink-50">Total</dt>
          <dd className="text-[15px] font-semibold tabular-nums text-ink-50">
            £{displayTotal.toFixed(2)}
          </dd>
          {isBelowMin ? (
            <dt className="col-span-2 text-[11px] text-ink-200">
              cleaning subtotal bumped to £{MIN_CHARGE.toFixed(2)}; travel
              added on top (display only — server writes true line totals)
            </dt>
          ) : null}
        </dl>
      </section>

      {itemsWithQty.length > 0 && displayTotal > 0 ? (
        <>
          {/* Aggregate stain-protector upsell — mirrors the prot-banner
              block in trengo/quick-form.php. Light purple fill + darker
              purple top accent bar, distinctly identifiable, sits above
              the sales script. */}
          {upsell.hasAny ? (
            <section className="border-y-2 border-slack-purple bg-slack-purple/10 px-6 py-3 text-[13px]">
              <div className="flex items-start gap-3 text-ink-50">
                <svg
                  viewBox="0 0 24 24"
                  className="mt-0.5 h-5 w-5 shrink-0 fill-slack-purple"
                  aria-hidden
                >
                  <path d="M12 2 4 5v6c0 5 3.4 9.3 8 11 4.6-1.7 8-6 8-11V5l-8-3Z" />
                </svg>
                <div className="flex-1 leading-snug">
                  With stain protection the price will be{" "}
                  <strong className="tabular-nums">
                    £{upsell.total.toFixed(2)}
                  </strong>
                  {upsellPctMore > 0 ? ` (${upsellPctMore}% more)` : ""}. Ask the
                  customer if they'd like it.
                </div>
                <button
                  onClick={() => setWhatToSayOpen(true)}
                  className="shrink-0 rounded-md border border-slack-purple/60 bg-slack-purple/20 px-2 py-0.5 text-[12px] font-medium uppercase tracking-wider text-ink-50 hover:bg-slack-purple/30"
                >
                  What to say
                </button>
              </div>
            </section>
          ) : null}

          {/* Sales script — quieter panel underneath. Ported from
              trengo/quick-form.php: opening pitch, the two closing
              questions (date-already-discussed vs availability), then the
              parking/lift/water checklist. The closing question is the
              critical one — skipping it loses sales. */}
          <section className="border-t border-ink-700 bg-ink-900/40 px-6 py-3 text-[13px]">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-200">
              Sales script
            </div>
            <div className="mt-1 italic leading-snug text-ink-50">
              "The total price will be{" "}
              <strong className="tabular-nums">£{displayTotal.toFixed(2)}</strong>{" "}
              for the deep cleaning and stain removal.
              {totalMinutes > 0 ? (
                <>
                  {" "}
                  The cleaning will take around{" "}
                  <strong>{timeRangeLabel(totalMinutes)}</strong> depending on
                  stain removal requirements, and drying time is usually around
                  4 to 5 hours.
                </>
              ) : null}
              "
            </div>
            <div className="mt-3 space-y-1.5 border-t border-ink-700 pt-3">
              <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-200">
                If the date is already discussed, say:
              </div>
              <div className="italic leading-snug text-ink-50">
                "Would you like me to get your deep cleaning booked in?"
              </div>
              <div className="mt-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-200">
                Otherwise, say:
              </div>
              <div className="italic leading-snug text-ink-50">
                "Would you like me to check availability for you?"
              </div>
            </div>
            <div className="mt-3 border-t border-ink-700 pt-3">
              <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-200">
                Have you asked about:
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                <ChecklistTile letter="P" title={'"Is there free parking available?"'} subtitle="We pass any charge to customer" />
                <ChecklistTile
                  icon={
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  }
                  title={'"What floor and is there a lift?"'}
                  subtitle="If flat, townhouse, or commercial"
                />
                <ChecklistTile
                  icon={
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                      <path d="M12 3 6 10c-2 2-2 6 0 8s6 2 8 0 2-6 0-8l-2-3" />
                    </svg>
                  }
                  title={'"Is there hot water available?"'}
                  subtitle="If moving in/out or commercial"
                />
              </div>
            </div>
          </section>
        </>
      ) : null}

      <footer className="flex items-center justify-between gap-3 border-t border-ink-700 px-6 py-3 text-[13px]">
        <div className="text-ink-200">
          {saveError ? (
            <span className="text-slack-pink">⚠ {saveError}</span>
          ) : dirty ? (
            <span className="text-slack-yellow">Unsaved changes</span>
          ) : (
            <span>Up to date</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={dirty ? onCancel : onClose}
            className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400"
          >
            {dirty ? "Discard" : "Close"}
          </button>
          <button
            onClick={onSave}
            disabled={!dirty || save.isPending}
            className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-40"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </footer>

      <CatalogPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={addCatalogItem}
      />

      <ItemDetailModal
        item={detailIdx !== null ? draft.items[detailIdx] ?? null : null}
        onClose={() => setDetailIdx(null)}
        onSave={(patch) => {
          if (detailIdx !== null) updateItem(detailIdx, patch);
          setDetailIdx(null);
        }}
      />

      <WhatToSayModal open={whatToSayOpen} onClose={() => setWhatToSayOpen(false)} />

      {quote.vid !== null && quote.cardId !== null ? (
        <NotifyModal
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          vid={quote.vid}
          cardId={quote.cardId}
          orderLabel={quote.visitNumberText ? `Order ${quote.visitNumberText}` : `Order ${quote.vid}`}
          customerName={
            [draft.contact.firstName, draft.contact.lastName]
              .filter(Boolean)
              .join(" ") || "Customer"
          }
        />
      ) : null}
    </>
  );
}

// Small icon/letter pill used in the sales-script "Have you asked about"
// checklist. Accepts a single-letter badge (`letter`) or a custom icon.
function ChecklistTile({
  letter,
  icon,
  title,
  subtitle,
}: {
  letter?: string;
  icon?: React.ReactNode;
  title: string;
  subtitle: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-md border border-ink-700 bg-ink-900/40 px-2 py-1.5 text-ink-50">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slack-purple/25 text-[13px] font-bold text-ink-50">
        {icon ?? letter}
      </span>
      <div className="min-w-0 flex-1 text-[12px] leading-snug">
        <div className="italic text-ink-50">{title}</div>
        <div className="text-ink-200">{subtitle}</div>
      </div>
    </div>
  );
}

function EditableRow({
  item,
  descriptionChanged,
  protector,
  onQtyChange,
  onRemove,
  onOpenDetails,
  onAddProtector,
}: {
  item: QuoteItem;
  descriptionChanged: boolean;
  protector?: CatalogItem | undefined;
  onQtyChange: (qty: number) => void;
  onRemove: () => void;
  onOpenDetails: () => void;
  onAddProtector?: (() => void) | undefined;
}): JSX.Element {
  const rawSub = item.quantity * item.unitPrice;
  const discount =
    item.discountType === "Percentage"
      ? rawSub * (item.discountPercent / 100)
      : item.discountAmount;
  const postDiscount = Math.max(0, rawSub - discount);
  const tax = item.isTaxable ? postDiscount * (item.taxRate / 100) : 0;
  const line = postDiscount + tax;

  return (
    <li className="group/row">
      {/* Main row — one line, consistent font. Clicking the name opens the
          details modal (condition / VAT / per-line discount live there).
          The Upgrade-to-Protector pill lives inline next to the name so
          staff see the upsell without the row wrapping to a second line. */}
      <div className="flex items-center gap-3 px-3 py-2 text-[13px]">
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <button
            onClick={onOpenDetails}
            className="min-w-0 truncate text-left font-medium text-ink-50 hover:underline"
            title={item.description ? `${item.name} — ${item.description}` : "Edit details"}
          >
            {item.name}
          </button>
          {descriptionChanged ? (
            <span
              className="inline-block shrink-0 rounded-full bg-slack-purple/20 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-ink-50"
              title="This line has a custom description (differs from the catalog default)"
            >
              Description changed
            </span>
          ) : null}
          {protector && onAddProtector ? (
            <button
              onClick={onAddProtector}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slack-purple/50 bg-slack-purple/15 px-2.5 py-0.5 text-[11px] font-medium text-ink-50 hover:bg-slack-purple/25"
              title={protector.description ?? protector.name}
            >
              <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current" aria-hidden>
                <path d="M12 2 4 5v6c0 5 3.4 9.3 8 11 4.6-1.7 8-6 8-11V5l-8-3Z" />
              </svg>
              Upgrade to Protector · £{protector.price.toFixed(2)}
            </button>
          ) : null}
        </div>
        {/* Qty stepper: +/− only, no native number-input spinners. */}
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={() => onQtyChange(Math.max(1, item.quantity - 1))}
            className="h-6 w-6 rounded border border-ink-500 bg-ink-800 text-ink-50 hover:border-ink-400"
            aria-label="Decrease"
          >
            −
          </button>
          <span className="w-8 text-center tabular-nums text-ink-50">
            {item.quantity}
          </span>
          <button
            onClick={() => onQtyChange(item.quantity + 1)}
            className="h-6 w-6 rounded border border-ink-500 bg-ink-800 text-ink-50 hover:border-ink-400"
            aria-label="Increase"
          >
            +
          </button>
        </div>
        <div className="w-20 shrink-0 text-right tabular-nums text-ink-200">
          × £{item.unitPrice.toFixed(2)}
        </div>
        {discount > 0 ? (
          <div
            className="w-16 shrink-0 text-right tabular-nums text-slack-yellow"
            title={
              item.discountType === "Percentage"
                ? `${item.discountPercent}% off`
                : `£${discount.toFixed(2)} off`
            }
          >
            −£{discount.toFixed(2)}
          </div>
        ) : null}
        <div className="w-20 shrink-0 text-right font-semibold tabular-nums text-ink-50">
          £{line.toFixed(2)}
        </div>
        <button
          onClick={onRemove}
          className="shrink-0 text-ink-200 hover:text-slack-pink"
          aria-label="Remove item"
          title="Remove item"
        >
          {/* Trash / bin icon */}
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      </div>
    </li>
  );
}

function CatalogPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (item: CatalogItem) => void;
}): JSX.Element {
  const cat = useCatalog(open);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!cat.data) return [];
    const term = q.trim().toLowerCase();
    return cat.data.categories
      .map((c) => ({
        ...c,
        items: c.items.filter((i) => {
          if (i.isProtector) return false; // protectors are pairing upsells, picked via main item
          if (!term) return true;
          return (
            i.name.toLowerCase().includes(term) ||
            (i.description ?? "").toLowerCase().includes(term)
          );
        }),
      }))
      .filter((c) => c.items.length > 0);
  }, [cat.data, q]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[60] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="picker"
            className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-2xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center gap-3 border-b border-ink-700 px-4 py-3">
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search items (e.g. bedroom, stairs, sofa)…"
                  className="flex-1 rounded-md border border-ink-500 bg-ink-900 px-3 py-1.5 text-[13px] text-ink-50 placeholder:text-ink-200"
                />
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                  aria-label="Close"
                >
                  Esc
                </button>
              </header>
              <div className="max-h-[70vh] overflow-y-auto px-2 py-3">
                {cat.isLoading ? (
                  <div className="px-4 py-8 text-center text-[13px] text-ink-200">
                    loading catalog…
                  </div>
                ) : cat.isError ? (
                  <div className="px-4 py-8 text-center text-[13px] text-slack-pink">
                    couldn't load catalog
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-4 py-8 text-center text-[13px] text-ink-200">
                    nothing matches "{q}"
                  </div>
                ) : (
                  filtered.map((c) => (
                    <div key={c.id} className="mb-4">
                      <div className="px-3 pb-1 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                        {c.name}
                      </div>
                      <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
                        {c.items.map((i) => (
                          <li key={i.chargemasterId}>
                            <button
                              onClick={() => onPick(i)}
                              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-ink-800/60"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-[13px] text-ink-50">
                                  {i.name}
                                </div>
                                {i.description ? (
                                  <div className="truncate text-[12px] text-ink-200">
                                    {i.description}
                                  </div>
                                ) : null}
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-[13px] font-medium tabular-nums text-ink-50">
                                  £{i.price.toFixed(2)}
                                </div>
                                {i.isTaxable ? (
                                  <div className="text-[12px] text-ink-200">
                                    VAT {i.taxRate}%
                                  </div>
                                ) : null}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

const CONDITIONS = ["Average", "Good", "Bad"] as const;

// Pre-computed list of "HH:MM" in 30-minute steps — feeds the Start/End
// time dropdowns. 48 entries plus the blank "not set" option.
const TIME_SLOTS: string[] = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

// "YYYY-MM-DD" ⇄ Date helpers. Use local-time Date objects so a calendar
// click on "22 April" stays "22 April" regardless of the viewer's
// timezone offset.
function ymdToDate(s: string): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mm, dd] = m;
  return new Date(Number(y), Number(mm) - 1, Number(dd));
}
function dateToYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW_SHORT = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

// Click-only popover calendar. Matches the UX in order-form.php: staff
// sees a readonly text input; clicking it toggles a month grid below;
// clicking a day fills the input and closes the popup. No typing, no
// browser-native quirks.
function DatePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (ymd: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  // Month currently displayed in the grid. Initialises from `value` if set,
  // otherwise today.
  const initial = ymdToDate(value) ?? new Date();
  const [cursor, setCursor] = useState<Date>(
    new Date(initial.getFullYear(), initial.getMonth(), 1)
  );
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Build the 6-row grid. Week starts Monday. Pad leading/trailing days
  // from neighbouring months (greyed out) so the layout never shifts.
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const weekday = (firstOfMonth.getDay() + 6) % 7; // Mon=0
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - weekday);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    cells.push(d);
  }

  const selected = ymdToDate(value);
  const today = new Date();
  const label = selected
    ? selected.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "Select a date";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-left text-[13px] text-ink-50 hover:border-ink-400"
      >
        <span>{label}</span>
        <span aria-hidden className="text-ink-200">📅</span>
      </button>
      {open ? (
        <div className="absolute left-0 z-50 mt-1 w-[18rem] rounded-md border border-ink-500 bg-ink-800 p-2 shadow-drag">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
              }
              className="rounded-md px-2 py-0.5 text-ink-50 hover:bg-ink-700"
              aria-label="Previous month"
            >
              ←
            </button>
            <div className="text-[13px] font-semibold text-ink-50">
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() =>
                setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
              }
              className="rounded-md px-2 py-0.5 text-ink-50 hover:bg-ink-700"
              aria-label="Next month"
            >
              →
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] uppercase tracking-wider text-ink-200">
            {DOW_SHORT.map((d) => (
              <div key={d} className="py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isSelected =
                !!selected &&
                d.getFullYear() === selected.getFullYear() &&
                d.getMonth() === selected.getMonth() &&
                d.getDate() === selected.getDate();
              const isToday =
                d.getFullYear() === today.getFullYear() &&
                d.getMonth() === today.getMonth() &&
                d.getDate() === today.getDate();
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  onClick={() => {
                    onChange(dateToYmd(d));
                    setOpen(false);
                  }}
                  className={`h-8 rounded-md text-[13px] tabular-nums ${
                    isSelected
                      ? "bg-slack-aubergine text-ink-50 font-semibold"
                      : isToday
                      ? "border border-slack-aubergineLight/60 text-ink-50 hover:bg-ink-700"
                      : inMonth
                      ? "text-ink-50 hover:bg-ink-700"
                      : "text-ink-300 hover:bg-ink-700/60"
                  }`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          {selected ? (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="mt-2 w-full rounded-md border border-ink-500 px-2 py-1 text-[12px] uppercase tracking-wider text-ink-50 hover:border-ink-400"
            >
              Clear date
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Visit-level status + schedule. Sits between the Contact/Location grid and
// the Line Items table. Drives the VisitsTab badge colour + scheduled-slot
// display: pick "Booking made (Converted)" + set a date/time and the visit
// flips from blue "Booking Not Made" to green "Booking Made" on save.
function ScheduleSection({
  vid,
  cardId,
  schedule,
  postcode,
  estimatedValueGbp,
  durationMinutes,
  onChange,
}: {
  vid: number | null;
  cardId: number | null;
  schedule: ScheduleDraft;
  postcode: string;
  estimatedValueGbp: number;
  durationMinutes: number;
  onChange: (patch: Partial<ScheduleDraft>) => void;
}): JSX.Element {
  const inputCls =
    "rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 placeholder:text-ink-200 focus:border-slack-aubergineLight focus:outline-none";

  // Dispatch Board (temporary bridge to the dev.breeze.services free-slot
  // iframe). Free-slot.php has been patched to postMessage a
  // 'dispatch.slot' payload on slot click; we listen here and mirror what
  // the legacy order-form.php parent._set_date() did: set status to
  // "Booking made (Converted)", date, start/end, technician.
  const [dispatchOpen, setDispatchOpen] = useState(false);

  // Find Slots — calls the FastAPI scheduling service via /api/scheduling/*
  // proxy. Returns top 4 ranked slots; clicking one populates the same fields
  // as the Dispatch Board (status / date / start / end / technician).
  const [findSlotsOpen, setFindSlotsOpen] = useState(false);

  // Dispatch Board 4 — full preferences/slots/day-grid view, opened from the
  // quote with postcode/duration/value pre-populated. Drag-to-reschedule lives
  // in here so a human handover from the agent has one place to manage.
  const [db4Open, setDb4Open] = useState(false);
  useEffect(() => {
    if (!dispatchOpen) return;
    function onMsg(ev: MessageEvent) {
      if (ev.origin !== "https://dev.breeze.services") return;
      const d = ev.data as {
        type?: string;
        cleanerId?: string | number;
        startTime?: string;
        endTime?: string;
        date?: string;
      } | null;
      if (!d || d.type !== "dispatch.slot") return;
      onChange({
        workOrderStatus: "Booking made (Converted)",
        scheduleDate: d.date ?? "",
        startTime: d.startTime ?? "",
        endTime: d.endTime ?? "",
        technicianId: d.cleanerId != null ? String(d.cleanerId) : "",
      });
      setDispatchOpen(false);
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [dispatchOpen, onChange]);

  // Technician list — populated from the postcode. Debounced so staff
  // typing a new address doesn't hammer the API; empty postcode shows
  // nothing (can't resolve a tech without an outward code).
  const [techs, setTechs] = useState<Technician[]>([]);
  const [techsLoading, setTechsLoading] = useState(false);
  useEffect(() => {
    if (!postcode.trim()) {
      setTechs([]);
      return;
    }
    let cancelled = false;
    setTechsLoading(true);
    const t = window.setTimeout(() => {
      fetchTechniciansForPostcode(postcode)
        .then((list) => {
          if (!cancelled) setTechs(list);
        })
        .catch(() => {
          if (!cancelled) setTechs([]);
        })
        .finally(() => {
          if (!cancelled) setTechsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [postcode]);

  // Keep the currently-assigned tech visible in the dropdown even if they
  // don't cover the postcode any more (e.g. the visit was booked before
  // their area changed). Prepended as a disabled-looking option.
  const selectedTechInList = techs.some(
    (t) => String(t.id) === schedule.technicianId
  );

  return (
    <section className="border-t border-ink-700 px-6 py-5">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Status &amp; schedule
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFindSlotsOpen(true)}
            disabled={!postcode.trim()}
            className="rounded-md border border-emerald-600 bg-emerald-700 px-2 py-1 text-[13px] font-medium text-ink-50 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              postcode.trim()
                ? "Find ranked slots via the scheduling service — click one to populate the schedule fields"
                : "Enter a service postcode first"
            }
          >
            Find slots
          </button>
          <button
            type="button"
            onClick={() => setDb4Open(true)}
            disabled={!postcode.trim()}
            className="rounded-md border border-emerald-600 bg-emerald-700 px-2 py-1 text-[13px] font-medium text-ink-50 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              postcode.trim()
                ? "Open Dispatch Board 4 — preferences, slot table, tech-column day grid, drag-to-reschedule"
                : "Enter a service postcode first"
            }
          >
            Dispatch Board
          </button>
          {vid !== null ? (
            <button
              type="button"
              onClick={() => setDispatchOpen(true)}
              className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
              title="Open legacy Dispatch Board 3 (iframe to dev.breeze.services) — click a slot to populate schedule fields"
            >
              Dispatch Board (old)
            </button>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[13px] text-ink-200">
            Work order status
          </span>
          <select
            value={schedule.workOrderStatus}
            onChange={(e) => onChange({ workOrderStatus: e.target.value })}
            className={`${inputCls} w-full`}
          >
            {WORK_ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
            {WORK_ORDER_STATUSES.includes(
              schedule.workOrderStatus as (typeof WORK_ORDER_STATUSES)[number]
            )
              ? null
              : (
                <option value={schedule.workOrderStatus}>
                  {schedule.workOrderStatus} (legacy)
                </option>
              )}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[13px] text-ink-200">Technician</span>
          <select
            value={schedule.technicianId}
            onChange={(e) => onChange({ technicianId: e.target.value })}
            className={`${inputCls} w-full`}
            disabled={!postcode.trim()}
          >
            <option value="">
              {postcode.trim()
                ? techsLoading
                  ? "Loading…"
                  : techs.length === 0
                    ? "No techs cover this postcode"
                    : "— Select a technician —"
                : "Enter a service postcode first"}
            </option>
            {/* Preserve the currently-assigned tech even if their coverage
                changed since the booking was made. */}
            {!selectedTechInList && schedule.technicianId ? (
              <option value={schedule.technicianId}>
                (assigned tech #{schedule.technicianId} — outside coverage)
              </option>
            ) : null}
            {techs.map((t) => {
              const name = t.nickName || t.firstName || `Tech #${t.id}`;
              return (
                <option key={t.id} value={String(t.id)}>
                  {name} · {t.shedMode} · {t.commissionPlan}%
                </option>
              );
            })}
          </select>
        </label>

        <div className="block">
          <span className="mb-1 block text-[13px] text-ink-200">
            Scheduled date
          </span>
          <DatePicker
            value={schedule.scheduleDate}
            onChange={(v) => onChange({ scheduleDate: v })}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-[13px] text-ink-200">Start time</span>
            <select
              value={schedule.startTime}
              onChange={(e) => onChange({ startTime: e.target.value })}
              className={`${inputCls} w-full`}
            >
              <option value="">—</option>
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[13px] text-ink-200">End time</span>
            <select
              value={schedule.endTime}
              onChange={(e) => onChange({ endTime: e.target.value })}
              className={`${inputCls} w-full`}
            >
              <option value="">—</option>
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {findSlotsOpen ? (
        <FindSlotsModal
          postcode={postcode}
          durationMinutes={durationMinutes}
          estimatedValueGbp={estimatedValueGbp}
          jobRef={vid !== null ? `VID-${vid}` : `card-new`}
          onPick={(s) => {
            onChange(s);
            setFindSlotsOpen(false);
          }}
          onClose={() => setFindSlotsOpen(false)}
        />
      ) : null}

      {db4Open ? (
        <DispatchBoard5
          onClose={() => setDb4Open(false)}
          cardId={cardId}
          initialPostcode={postcode}
          initialDurationMinutes={durationMinutes}
          initialEstimatedValueGbp={estimatedValueGbp}
          initialDate={schedule.scheduleDate}
          onPickSlot={(pick) => {
            onChange({
              workOrderStatus: "Booking made (Converted)",
              scheduleDate: pick.scheduleDate,
              startTime: pick.startTime,
              endTime: pick.endTime,
              technicianId: String(pick.techId),
            });
          }}
        />
      ) : null}

      {dispatchOpen && vid !== null ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setDispatchOpen(false)}
        >
          <div
            className="flex h-[90vh] w-[min(1200px,96vw)] flex-col overflow-hidden rounded-lg border border-ink-500 bg-ink-900 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-ink-700 px-4 py-2">
              <div className="text-[13px] font-medium text-ink-50">
                Dispatch Board 3 — click a green slot to book
              </div>
              <button
                type="button"
                onClick={() => setDispatchOpen(false)}
                className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:border-ink-400"
              >
                Close
              </button>
            </div>
            <iframe
              title="Dispatch Board"
              src={(() => {
                const u = new URLSearchParams({
                  id: String(vid),
                  dispatch3: "1",
                  popup: "1",
                  country: "",
                });
                // schedule.php reads postcode + date from query params to
                // filter the slot grid (it doesn't derive them from vid).
                if (postcode.trim()) u.set("postcode", postcode.trim());
                if (schedule.scheduleDate) {
                  // order-form.php passes the UK-formatted schedule_date
                  // (dd/mm/yyyy) as the `date` param. Our draft stores ISO
                  // yyyy-mm-dd — convert.
                  const [y, m, d] = schedule.scheduleDate.split("-");
                  if (y && m && d) u.set("date", `${d}/${m}/${y}`);
                }
                return `https://dev.breeze.services/schedule.php?${u.toString()}`;
              })()}
              className="h-full w-full flex-1 bg-white"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

// Tech notes — B_TTECHNOTES feed + composer, ported from
// tech-order-form.php. Displays newest-first with bold timestamp + author,
// renders note body as HTML (trusted internal content — staff-authored).
function TechNotesSection({ vid }: { vid: number }): JSX.Element {
  const qc = useQueryClient();
  const notes = useQuery({
    queryKey: ["tech-notes", vid],
    queryFn: () => fetchTechNotes(vid),
  });
  const add = useMutation({
    mutationFn: (htmlNotes: string) => postTechNote(vid, htmlNotes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tech-notes", vid] });
    },
  });
  const [draft, setDraft] = useState("");

  function formatWhen(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const day = d.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${day} ${time}`;
  }

  function submit(): void {
    const trimmed = draft.trim();
    if (!trimmed) return;
    add.mutate(trimmed, {
      onSuccess: () => setDraft(""),
    });
  }

  return (
    <section className="border-t border-ink-700 px-6 py-5">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
        Tech notes
      </div>

      <div className="mb-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Add a note for the technician or the team…"
          rows={3}
          className="w-full resize-y rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50 placeholder:text-ink-300 focus:border-slack-aubergineLight focus:outline-none"
        />
        <div className="mt-1.5 flex items-center justify-between">
          <div className="text-[11px] text-ink-300">
            {add.isError ? (
              <span className="text-red-400">
                {(add.error as Error).message || "Save failed"}
              </span>
            ) : (
              <span>&#8984;&thinsp;↵ to save</span>
            )}
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || add.isPending}
            className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
          >
            {add.isPending ? "Saving…" : "Add Note"}
          </button>
        </div>
      </div>

      {notes.isLoading ? (
        <div className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-4 py-4 text-center text-[12px] text-ink-200">
          Loading…
        </div>
      ) : notes.data && notes.data.length > 0 ? (
        <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
          {notes.data.map((n) => (
            <li key={n.id} className="px-3 py-2">
              <div className="mb-1 text-[11px] font-semibold text-ink-200">
                {formatWhen(n.timestamp)} · {n.authorName}
              </div>
              <div
                className="tech-note-body text-[13px] text-ink-50"
                dangerouslySetInnerHTML={{ __html: n.htmlNotes }}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-4 py-4 text-center text-[12px] text-ink-200">
          No tech notes on this visit yet.
        </div>
      )}
    </section>
  );
}

function ContactSection({
  contact,
  selectedCid,
  options,
  aid,
  cardId,
  differentBilling,
  billingCid,
  onChange,
  onPickExisting,
  onContactCreated,
  onToggleDifferentBilling,
  onPickBilling,
}: {
  contact: ContactDraft;
  selectedCid: number | null;
  options: AccountContact[];
  aid: number;
  cardId: number | null;
  differentBilling: boolean;
  billingCid: number | null;
  onChange: (patch: Partial<ContactDraft>) => void;
  onPickExisting: (c: AccountContact) => void;
  onContactCreated: (newCid: number, fields: ContactDraft) => void;
  onToggleDifferentBilling: (on: boolean) => void;
  onPickBilling: (cid: number) => void;
}): JSX.Element {
  const inputCls =
    "w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 placeholder:text-ink-200 focus:border-slack-aubergineLight focus:outline-none";
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Customer
        </div>
        <div className="flex items-center gap-1.5">
          {options.length > 1 ? (
            <select
              className="rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
              value={selectedCid ?? ""}
              onChange={(e) => {
                const cid = Number(e.target.value);
                const picked = options.find((o) => o.cid === cid);
                if (picked) onPickExisting(picked);
              }}
              title="Switch this visit to a different existing contact on the account cluster"
            >
              {options.map((o) => (
                <option key={o.cid} value={o.cid}>
                  {o.fullName ||
                    `${o.firstName} ${o.lastName}`.trim() ||
                    `Contact #${o.cid}`}
                </option>
              ))}
            </select>
          ) : null}
          <AddContactInline
            aid={aid}
            cardId={cardId}
            onCreated={onContactCreated}
          />
        </div>
      </div>
      <div className="grid grid-cols-[6rem_1fr] items-center gap-x-2 gap-y-1.5 text-[13px]">
        <label className="text-ink-200">First</label>
        <input
          className={inputCls}
          value={contact.firstName}
          onChange={(e) => onChange({ firstName: e.target.value })}
        />
        <label className="text-ink-200">Last</label>
        <input
          className={inputCls}
          value={contact.lastName}
          onChange={(e) => onChange({ lastName: e.target.value })}
        />
        <label className="text-ink-200">Email</label>
        <input
          type="email"
          className={inputCls}
          value={contact.email}
          onChange={(e) => onChange({ email: e.target.value })}
        />
        <label className="text-ink-200">Mobile</label>
        <div className="flex items-center gap-2">
          <input
            type="tel"
            className={`${inputCls} flex-1`}
            value={contact.phoneMobile}
            onChange={(e) =>
              onChange({ phoneMobile: e.target.value, phone: e.target.value })
            }
          />
          <CallButton number={contact.phoneMobile} />
        </div>
        <label className="text-ink-200">Home</label>
        <div className="flex items-center gap-2">
          <input
            type="tel"
            className={`${inputCls} flex-1`}
            value={contact.phoneHome}
            onChange={(e) => onChange({ phoneHome: e.target.value })}
          />
          <CallButton number={contact.phoneHome} />
        </div>
        <label className="text-ink-200">Work</label>
        <div className="flex items-center gap-2">
          <input
            type="tel"
            className={`${inputCls} flex-1`}
            value={contact.phoneWork}
            onChange={(e) => onChange({ phoneWork: e.target.value })}
          />
          <CallButton number={contact.phoneWork} />
        </div>
        <label className="text-ink-200">Other</label>
        <div className="flex items-center gap-2">
          <input
            type="tel"
            className={`${inputCls} flex-1`}
            value={contact.phoneOther}
            onChange={(e) => onChange({ phoneOther: e.target.value })}
          />
          <CallButton number={contact.phoneOther} />
        </div>
      </div>
      <label className="mt-3 flex items-center gap-2 text-[12px] text-ink-200">
        <input
          type="checkbox"
          checked={differentBilling}
          onChange={(e) => onToggleDifferentBilling(e.target.checked)}
        />
        Different billing contact
      </label>
      {differentBilling ? (
        <div className="mt-1.5 flex items-center gap-2 pl-5 text-[12px]">
          <span className="text-ink-200">Bill to:</span>
          {options.length > 0 ? (
            <select
              className="rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
              value={billingCid ?? ""}
              onChange={(e) => {
                const cid = Number(e.target.value);
                if (Number.isFinite(cid) && cid > 0) onPickBilling(cid);
              }}
              title="Pick the contact the invoice is billed to"
            >
              <option value="">— pick a contact —</option>
              {options.map((o) => (
                <option key={o.cid} value={o.cid}>
                  {o.fullName ||
                    `${o.firstName} ${o.lastName}`.trim() ||
                    `Contact #${o.cid}`}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-ink-300">
              No other contacts on this account.
            </span>
          )}
        </div>
      ) : null}
    </section>
  );
}

// Inline "+ New" contact button. Opens a small modal with the same fields
// the QuoteEditor renders for an existing contact, creates the contact on
// the visit's account via /api/accounts/:aid/contacts, and hands the new
// CID + the typed values back to the parent so the form fields populate
// and the visit's SERVICECONTACTID switches to the new row on save.
function AddContactInline({
  aid,
  cardId,
  onCreated,
}: {
  aid: number;
  cardId: number | null;
  onCreated: (newCid: number, fields: ContactDraft) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [mob, setMob] = useState("");
  const [home, setHome] = useState("");
  const [work, setWork] = useState("");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inputCls =
    "w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 placeholder:text-ink-200 focus:border-slack-aubergineLight focus:outline-none";

  const canSubmit =
    (first.trim() || last.trim() || email.trim() || mob.trim()) && !busy;

  function close(): void {
    setOpen(false);
    setFirst("");
    setLast("");
    setEmail("");
    setMob("");
    setHome("");
    setWork("");
    setOther("");
    setErr(null);
    setBusy(false);
  }

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const created = await createAccountContact(aid, {
        firstName: first.trim() || "*",
        lastName: last.trim(),
        email: email.trim(),
        phone: mob.trim(),
        phoneMobile: mob.trim(),
        phoneHome: home.trim(),
        phoneWork: work.trim(),
        phoneOther: other.trim(),
      });
      // Refresh whichever cluster query is feeding the picker so the new
      // contact appears in the dropdown immediately.
      if (cardId !== null) {
        await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      } else {
        await qc.invalidateQueries({ queryKey: ["accountCluster", aid] });
      }
      onCreated(created.cid, {
        firstName: first.trim() || "*",
        lastName: last.trim(),
        email: email.trim(),
        phone: mob.trim(),
        phoneMobile: mob.trim(),
        phoneHome: home.trim(),
        phoneWork: work.trim(),
        phoneOther: other.trim(),
      });
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-ink-500 bg-ink-800 px-2 py-1 text-[12px] font-medium text-ink-50 hover:border-ink-400 hover:bg-ink-700"
      >
        + New
      </button>
      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              key="s"
              className="fixed inset-0 z-[85] bg-ink-950/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={busy ? undefined : close}
            />
            <motion.div
              key="m"
              className="fixed inset-0 z-[85] flex items-center justify-center p-4"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
            >
              <div
                className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-center justify-between border-b border-ink-700 px-4 py-3">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    New contact
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    disabled={busy}
                    className="rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1 text-[13px] text-ink-50 hover:border-ink-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </header>
                <div className="grid grid-cols-[6rem_1fr] items-center gap-x-2 gap-y-1.5 px-4 py-3 text-[13px]">
                  <label className="text-ink-200">First</label>
                  <input
                    autoFocus
                    className={inputCls}
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                  />
                  <label className="text-ink-200">Last</label>
                  <input
                    className={inputCls}
                    value={last}
                    onChange={(e) => setLast(e.target.value)}
                  />
                  <label className="text-ink-200">Email</label>
                  <input
                    type="email"
                    className={inputCls}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <label className="text-ink-200">Mobile</label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={mob}
                    onChange={(e) => setMob(e.target.value)}
                  />
                  <label className="text-ink-200">Home</label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={home}
                    onChange={(e) => setHome(e.target.value)}
                  />
                  <label className="text-ink-200">Work</label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={work}
                    onChange={(e) => setWork(e.target.value)}
                  />
                  <label className="text-ink-200">Other</label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                  />
                </div>
                {err ? (
                  <div className="px-4 pb-2 text-[12px] text-slack-pink">
                    ⚠ {err}
                  </div>
                ) : null}
                <footer className="flex justify-end gap-2 border-t border-ink-700 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={!canSubmit}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy ? "Creating…" : "Create contact"}
                  </button>
                </footer>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function CallButton({ number }: { number: string }): JSX.Element {
  const [state, setState] = useState<"idle" | "calling" | "ringing" | "error">(
    "idle"
  );
  const trimmed = (number ?? "").trim();
  async function handle(): Promise<void> {
    if (!trimmed || state !== "idle") return;
    setState("calling");
    try {
      await initiateCall(trimmed);
      setState("ringing");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 4000);
    }
  }
  const label =
    state === "calling"
      ? "Calling…"
      : state === "ringing"
        ? "Ringing your phone…"
        : state === "error"
          ? "Failed"
          : "📞 Call";
  return (
    <button
      type="button"
      onClick={() => void handle()}
      disabled={!trimmed || state !== "idle"}
      className={`shrink-0 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors ${
        state === "error"
          ? "border-slack-pink/60 bg-slack-pink/15 text-slack-pink"
          : state === "idle"
            ? "border-slack-cyan/50 bg-slack-cyan/10 text-slack-cyan hover:bg-slack-cyan/20"
            : "border-ink-500 bg-ink-800 text-ink-200"
      } disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {label}
    </button>
  );
}

// Inline "+ New" address button. Opens a modal with PCA postcode lookup +
// Line 1/2/3/City/Postcode inputs, creates the location on the account
// via /api/accounts/:aid/locations, and hands the new LID + fields back
// to the parent so the address fields populate and the visit's
// SERVICELOCATIONID switches on save.
function AddLocationInline({
  aid,
  cardId,
  onCreated,
}: {
  aid: number;
  cardId: number | null;
  onCreated: (newLid: number, fields: LocationDraft) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [a1, setA1] = useState("");
  const [a2, setA2] = useState("");
  const [a3, setA3] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [pcaResults, setPcaResults] = useState<PcaFindItem[]>([]);
  const [pcaState, setPcaState] = useState<"idle" | "loading">("idle");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inputCls =
    "w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 placeholder:text-ink-200 focus:border-slack-aubergineLight focus:outline-none";

  function close(): void {
    setOpen(false);
    setA1("");
    setA2("");
    setA3("");
    setCity("");
    setPostcode("");
    setPcaResults([]);
    setPcaState("idle");
    setErr(null);
    setBusy(false);
  }

  async function runFind(): Promise<void> {
    const term = postcode.trim() || [a1, city].filter(Boolean).join(" ").trim();
    if (!term) {
      setErr("Enter a postcode or address first");
      return;
    }
    setPcaState("loading");
    setErr(null);
    try {
      setPcaResults(await pcaFind(term));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "lookup failed");
    } finally {
      setPcaState("idle");
    }
  }

  async function pickPca(id: string): Promise<void> {
    try {
      const a = await pcaRetrieve(id);
      if (!a) {
        setErr("Retrieve failed");
        return;
      }
      setA1(a.Line1);
      setA2(a.Line2);
      setA3(a.Line3);
      setCity(a.City);
      setPostcode(a.PostalCode.toUpperCase());
      setPcaResults([]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "retrieve failed");
    }
  }

  const canSubmit = (a1.trim() || postcode.trim()) && !busy;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const created = await createAccountLocation(aid, {
        address1: a1.trim(),
        address2: a2.trim(),
        address3: a3.trim(),
        city: city.trim(),
        postcode: postcode.trim().toUpperCase(),
      });
      if (cardId !== null) {
        await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      } else {
        await qc.invalidateQueries({ queryKey: ["accountCluster", aid] });
      }
      onCreated(created.lid, {
        address1: a1.trim(),
        address2: a2.trim(),
        address3: a3.trim(),
        city: city.trim(),
        postcode: postcode.trim().toUpperCase(),
      });
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-ink-500 bg-ink-800 px-2 py-1 text-[12px] font-medium text-ink-50 hover:border-ink-400 hover:bg-ink-700"
      >
        + New
      </button>
      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              key="s"
              className="fixed inset-0 z-[85] bg-ink-950/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={busy ? undefined : close}
            />
            <motion.div
              key="m"
              className="fixed inset-0 z-[85] flex items-center justify-center p-4"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
            >
              <div
                className="w-full max-w-lg rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-center justify-between border-b border-ink-700 px-4 py-3">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    New address
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    disabled={busy}
                    className="rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1 text-[13px] text-ink-50 hover:border-ink-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </header>
                <div className="px-4 py-3">
                  <div className="mb-2 flex gap-2">
                    <input
                      autoFocus
                      placeholder="Find by postcode or street…"
                      className={`${inputCls} flex-1`}
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void runFind();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => void runFind()}
                      disabled={pcaState === "loading"}
                      className="shrink-0 rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1 text-[12px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
                    >
                      {pcaState === "loading" ? "Finding…" : "Find"}
                    </button>
                  </div>
                  {pcaResults.length > 0 ? (
                    <ul className="mb-3 max-h-40 overflow-y-auto rounded-md border border-ink-700 bg-ink-900/40">
                      {pcaResults.map((r) => (
                        <li
                          key={r.Id}
                          onClick={() => void pickPca(r.Id)}
                          className="cursor-pointer px-2 py-1 text-[12px] text-ink-50 hover:bg-ink-700"
                        >
                          <span className="font-medium">{r.Text}</span>
                          {r.Description ? (
                            <span className="ml-2 text-ink-200">
                              · {r.Description}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="grid grid-cols-[6rem_1fr] items-center gap-x-2 gap-y-1.5 text-[13px]">
                    <label className="text-ink-200">Line 1</label>
                    <input
                      className={inputCls}
                      value={a1}
                      onChange={(e) => setA1(e.target.value)}
                    />
                    <label className="text-ink-200">Line 2</label>
                    <input
                      className={inputCls}
                      value={a2}
                      onChange={(e) => setA2(e.target.value)}
                    />
                    <label className="text-ink-200">Line 3</label>
                    <input
                      className={inputCls}
                      value={a3}
                      onChange={(e) => setA3(e.target.value)}
                    />
                    <label className="text-ink-200">City</label>
                    <input
                      className={inputCls}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                    <label className="text-ink-200">Postcode</label>
                    <input
                      className={inputCls}
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                    />
                  </div>
                </div>
                {err ? (
                  <div className="px-4 pb-2 text-[12px] text-slack-pink">
                    ⚠ {err}
                  </div>
                ) : null}
                <footer className="flex justify-end gap-2 border-t border-ink-700 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={!canSubmit}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy ? "Creating…" : "Create address"}
                  </button>
                </footer>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function LocationSection({
  location,
  selectedLid,
  options,
  aid,
  cardId,
  differentBilling,
  billingLid,
  onChange,
  onApplyPcaAddress,
  onPickExisting,
  onLocationCreated,
  onToggleDifferentBilling,
  onPickBilling,
}: {
  location: LocationDraft;
  selectedLid: number | null;
  options: AccountLocation[];
  aid: number;
  cardId: number | null;
  differentBilling: boolean;
  billingLid: number | null;
  onChange: (patch: Partial<LocationDraft>) => void;
  onApplyPcaAddress: (a: Partial<LocationDraft>) => void;
  onPickExisting: (l: AccountLocation) => void;
  onLocationCreated: (newLid: number, fields: LocationDraft) => void;
  onToggleDifferentBilling: (on: boolean) => void;
  onPickBilling: (lid: number) => void;
}): JSX.Element {
  const [findState, setFindState] = useState<"idle" | "loading" | "error">("idle");
  const [findResults, setFindResults] = useState<PcaFindItem[]>([]);
  const [findError, setFindError] = useState<string | null>(null);

  const inputCls =
    "w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 placeholder:text-ink-200 focus:border-slack-aubergineLight focus:outline-none";

  async function runFind(): Promise<void> {
    const term = location.postcode.trim();
    if (!term) {
      setFindError("Enter a postcode first");
      setFindState("error");
      return;
    }
    setFindState("loading");
    setFindError(null);
    try {
      const items = await pcaFind(term);
      setFindResults(items);
      setFindState("idle");
      if (items.length === 0) setFindError("No addresses found");
    } catch (err) {
      setFindError(err instanceof Error ? err.message : "lookup failed");
      setFindState("error");
    }
  }

  async function pickResult(id: string): Promise<void> {
    try {
      const a = await pcaRetrieve(id);
      if (!a) {
        setFindError("Address details unavailable");
        return;
      }
      // Company → line 1, shifting the rest down (matches quick-form behaviour).
      if (a.Company) {
        onApplyPcaAddress({
          address1: a.Company,
          address2: a.Line1 || "",
          address3: a.Line2 || "",
          city: a.City || "",
          postcode: a.PostalCode || "",
        });
      } else {
        onApplyPcaAddress({
          address1: a.Line1 || "",
          address2: a.Line2 || "",
          address3: a.Line3 || "",
          city: a.City || "",
          postcode: a.PostalCode || "",
        });
      }
      setFindResults([]);
    } catch (err) {
      setFindError(err instanceof Error ? err.message : "lookup failed");
    }
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Service address
        </div>
        <div className="flex items-center gap-1.5">
          {options.length > 1 ? (
            <select
              className="rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
              value={selectedLid ?? ""}
              onChange={(e) => {
                const lid = Number(e.target.value);
                const picked = options.find((o) => o.lid === lid);
                if (picked) onPickExisting(picked);
              }}
              title="Switch this visit to a different existing address on the account cluster"
            >
              {options.map((o) => (
                <option key={o.lid} value={o.lid}>
                  {[o.postcode, o.address1].filter(Boolean).join(" · ") ||
                    `Address #${o.lid}`}
                </option>
              ))}
            </select>
          ) : null}
          <AddLocationInline
            aid={aid}
            cardId={cardId}
            onCreated={onLocationCreated}
          />
        </div>
      </div>
      <div className="grid grid-cols-[6rem_1fr] items-center gap-x-2 gap-y-1.5 text-[13px]">
        <label className="text-ink-200">Line 1</label>
        <input
          className={inputCls}
          value={location.address1}
          onChange={(e) => onChange({ address1: e.target.value })}
        />
        <label className="text-ink-200">Line 2</label>
        <input
          className={inputCls}
          value={location.address2}
          onChange={(e) => onChange({ address2: e.target.value })}
        />
        <label className="text-ink-200">Line 3</label>
        <input
          className={inputCls}
          value={location.address3}
          onChange={(e) => onChange({ address3: e.target.value })}
        />
        <label className="text-ink-200">City</label>
        <input
          className={inputCls}
          value={location.city}
          onChange={(e) => onChange({ city: e.target.value })}
        />
        <label className="text-ink-200">Postcode</label>
        <input
          className={inputCls}
          value={location.postcode}
          onChange={(e) => onChange({ postcode: e.target.value.toUpperCase() })}
        />
        <div />
        <button
          onClick={runFind}
          disabled={findState === "loading"}
          className="justify-self-start rounded-md border border-ink-500 bg-ink-800 px-2 py-0.5 text-[12px] uppercase tracking-wider text-ink-50 hover:border-slack-aubergineLight disabled:opacity-40"
        >
          {findState === "loading" ? "Searching…" : "Find address"}
        </button>
      </div>
      {findError ? (
        <div className="mt-2 rounded-md border border-slack-pink/30 bg-slack-pink/10 px-2 py-1 text-[13px] text-slack-pink">
          {findError}
        </div>
      ) : null}
      {findResults.length > 0 ? (
        <ul className="mt-2 max-h-48 overflow-y-auto divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40 text-[12px]">
          {findResults.map((r) => (
            <li key={r.Id}>
              <button
                onClick={() => pickResult(r.Id)}
                className="block w-full px-2 py-1.5 text-left text-ink-50 hover:bg-ink-800/60"
              >
                <div className="truncate">{r.Text}</div>
                {r.Description ? (
                  <div className="truncate text-[12px] text-ink-200">
                    {r.Description}
                  </div>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <label className="mt-3 flex items-center gap-2 text-[12px] text-ink-200">
        <input
          type="checkbox"
          checked={differentBilling}
          onChange={(e) => onToggleDifferentBilling(e.target.checked)}
        />
        Different billing address
      </label>
      {differentBilling ? (
        <div className="mt-1.5 flex items-center gap-2 pl-5 text-[12px]">
          <span className="text-ink-200">Bill to:</span>
          {options.length > 0 ? (
            <select
              className="rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
              value={billingLid ?? ""}
              onChange={(e) => {
                const lid = Number(e.target.value);
                if (Number.isFinite(lid) && lid > 0) onPickBilling(lid);
              }}
              title="Pick the address the invoice is billed to"
            >
              <option value="">— pick an address —</option>
              {options.map((o) => (
                <option key={o.lid} value={o.lid}>
                  {[o.postcode, o.address1].filter(Boolean).join(" · ") ||
                    `Address #${o.lid}`}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-ink-300">
              No other addresses on this account.
            </span>
          )}
        </div>
      ) : null}
    </section>
  );
}

function ItemDetailModal({
  item,
  onClose,
  onSave,
}: {
  item: QuoteItem | null;
  onClose: () => void;
  onSave: (patch: Partial<QuoteItem>) => void;
}): JSX.Element {
  // Local controlled state — only flows back to the draft on Save.
  const [description, setDescription] = useState("");
  const [condition, setCondition] = useState<string>("Average");
  const [unitPrice, setUnitPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [discountType, setDiscountType] = useState<"Fixed Amount" | "Percentage">(
    "Fixed Amount"
  );
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountSplit, setDiscountSplit] = useState<"breeze" | "split">(
    "breeze"
  );
  const [isTaxable, setIsTaxable] = useState(true);

  useEffect(() => {
    if (!item) return;
    setDescription(item.description ?? "");
    setCondition(item.itemCondition ?? "Average");
    setUnitPrice(item.unitPrice);
    setQuantity(item.quantity || 1);
    setDiscountType(item.discountType ?? "Fixed Amount");
    setDiscountAmount(item.discountAmount);
    setDiscountPercent(item.discountPercent);
    setDiscountSplit(item.discountSplit ?? "breeze");
    setIsTaxable(item.isTaxable);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [item, onClose]);

  // Live preview of line total inside the modal. Tax follows the local
  // VAT toggle so the preview reflects exactly what Save will write back.
  const rawSub = quantity * unitPrice;
  const d = discountType === "Percentage" ? rawSub * (discountPercent / 100) : discountAmount;
  const postDiscount = Math.max(0, rawSub - d);
  const tax = isTaxable ? postDiscount * ((item?.taxRate ?? 20) / 100) : 0;
  const lineTotal = postDiscount + tax;

  function save(): void {
    onSave({
      description,
      itemCondition: condition,
      unitPrice,
      quantity,
      discountType,
      discountAmount: discountType === "Fixed Amount" ? discountAmount : 0,
      discountPercent: discountType === "Percentage" ? discountPercent : 0,
      // No discount → split mode is irrelevant; force "breeze" so we don't
      // accidentally save a stale split flag against a zero-discount line.
      discountSplit: d > 0 ? discountSplit : "breeze",
      isTaxable,
    });
  }

  return (
    <AnimatePresence>
      {item ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[60] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="detail"
            className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-lg rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center justify-between gap-3 border-b border-ink-700 px-5 py-3">
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-50">
                    Details
                  </div>
                  <h3 className="mt-0.5 text-lg leading-tight text-ink-50">
                    {item.name}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>

              <div className="space-y-3 px-5 py-4 text-[13px]">
                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Description
                  </span>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-ink-500 bg-ink-900 px-2.5 py-1.5 text-[13px] text-ink-50"
                  />
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                      Condition
                    </span>
                    <select
                      value={condition}
                      onChange={(e) => setCondition(e.target.value)}
                      className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                    >
                      {CONDITIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                      Unit price (inc VAT)
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={unitPrice}
                      onChange={(e) =>
                        setUnitPrice(Math.max(0, parseFloat(e.target.value) || 0))
                      }
                      className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] tabular-nums text-ink-50"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Quantity
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))
                    }
                    className="w-28 rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] tabular-nums text-ink-50"
                  />
                </label>

                <div className="grid grid-cols-3 gap-3">
                  <label className="block">
                    <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                      Discount type
                    </span>
                    <select
                      value={discountType}
                      onChange={(e) =>
                        setDiscountType(
                          e.target.value as "Fixed Amount" | "Percentage"
                        )
                      }
                      className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                    >
                      <option value="Fixed Amount">Fixed</option>
                      <option value="Percentage">Percentage</option>
                    </select>
                  </label>
                  {discountType === "Fixed Amount" ? (
                    <label className="block col-span-2">
                      <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                        Discount £
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={discountAmount}
                        onChange={(e) =>
                          setDiscountAmount(
                            Math.max(0, parseFloat(e.target.value) || 0)
                          )
                        }
                        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] tabular-nums text-ink-50"
                      />
                    </label>
                  ) : (
                    <label className="block col-span-2">
                      <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                        Discount %
                      </span>
                      <input
                        type="number"
                        step="1"
                        min={0}
                        max={100}
                        value={discountPercent}
                        onChange={(e) =>
                          setDiscountPercent(
                            Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0))
                          )
                        }
                        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] tabular-nums text-ink-50"
                      />
                    </label>
                  )}
                </div>

                {d > 0 ? (
                  <div className="rounded-md border border-slack-yellow/40 bg-slack-yellow/5 px-3 py-2">
                    <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                      Discount absorbed by
                    </div>
                    <div className="flex gap-3 text-[13px] text-ink-50">
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="discount-split"
                          value="breeze"
                          checked={discountSplit === "breeze"}
                          onChange={() => setDiscountSplit("breeze")}
                          className="accent-slack-aubergineLight"
                        />
                        <span>
                          Breeze{" "}
                          <span className="text-ink-300">
                            (full hit; tech earnings unchanged)
                          </span>
                        </span>
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="discount-split"
                          value="split"
                          checked={discountSplit === "split"}
                          onChange={() => setDiscountSplit("split")}
                          className="accent-slack-aubergineLight"
                        />
                        <span>
                          Split 50/50{" "}
                          <span className="text-ink-300">
                            (tech absorbs £{(d / 2).toFixed(2)})
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                ) : null}

                <label className="flex items-center gap-2 text-[12px] text-ink-50">
                  <input
                    type="checkbox"
                    checked={isTaxable}
                    onChange={(e) => setIsTaxable(e.target.checked)}
                    className="h-4 w-4 accent-slack-aubergineLight"
                  />
                  <span className="font-semibold uppercase tracking-[0.18em] text-ink-200">
                    VAT applies
                  </span>
                  <span className="text-ink-300">
                    (default {item.isTaxable ? "on" : "off"} from catalogue)
                  </span>
                </label>

                <div className="mt-2 flex items-center justify-between rounded-md border border-ink-700 bg-ink-900/60 px-3 py-2 text-[12px]">
                  <div className="text-ink-200">
                    Discount −£<span className="tabular-nums">{d.toFixed(2)}</span>
                    {isTaxable ? (
                      <> · VAT £<span className="tabular-nums">{tax.toFixed(2)}</span></>
                    ) : null}
                  </div>
                  <div className="font-medium text-ink-50">
                    Line total £<span className="tabular-nums">{lineTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <footer className="flex items-center justify-end gap-2 border-t border-ink-700 px-5 py-3">
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
                >
                  Apply
                </button>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function WhatToSayModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[70] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="what"
            className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center justify-between gap-3 border-b border-ink-700 px-5 py-3">
                <h3 className="text-lg text-ink-50">
                  How to sell stain protector
                </h3>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>
              <div className="space-y-3 px-5 py-4 text-[13px] text-ink-50">
                <p className="text-ink-200">
                  After discussing the cleaning, naturally mention the protector:
                </p>
                <p>
                  <em>
                    "A lot of our customers also choose to have stain protector
                    applied after the cleaning. It creates an invisible barrier on
                    the fibres that helps repel spills and everyday dirt, so your
                    carpets stay looking fresher for much longer between cleans."
                  </em>
                </p>
                <p>
                  <em>
                    "It's particularly popular for families with children or pets.
                    The treatment typically adds about 40% to the cleaning cost,
                    but most customers find it really extends the life of the
                    clean by several months."
                  </em>
                </p>
                <p>
                  <em>
                    "Would you like me to include protector in your quote so you
                    can see the difference in price?"
                  </em>
                </p>
                <p className="text-[12px] text-ink-200">
                  If they say yes, use the ↑ Upgrade to Protector button on each
                  line and the upgraded prices will roll into the quote.
                </p>
              </div>
              <footer className="flex items-center justify-end gap-2 border-t border-ink-700 px-5 py-3">
                <button
                  onClick={onClose}
                  className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
                >
                  Got it
                </button>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
